import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { PaginatorComponent } from "../../../shared/components/paginator/paginator.component";
import { finalize, takeUntil } from 'rxjs/operators';
import { TransactionsService, PayslipCompanyStatus } from '../../../core/services/api-services/transactions.service';
import { CompanyWithPayslips, CixorPaymentPayslipSummary, CixorPaymentPayslipDetail, InvoicePaymentLedgerItem } from '../../../core/models/company-with-payslips.model';
import { AuthService } from '../../../core/services/client-services/auth.service';
import { Subject, Subscription } from 'rxjs';
import { BreadcrumbItem, BreadcrumbComponent } from '../../../shared/components/breadcrumb/breadcrumb.component';
import { BreadcrumbService } from '../../../shared/components/breadcrumb/service/breadcrumb.service';
import { ToastrService } from 'ngx-toastr';

declare var bootstrap: any;

@Component({
  selector: 'app-cixor-payment',
  standalone: true,
  imports: [CommonModule, FormsModule, PaginatorComponent, BreadcrumbComponent],
  providers: [DatePipe],
  templateUrl: './cixor-payment.component.html',
  styleUrl: './cixor-payment.component.css'
})
export class CreditLineComponent implements OnInit, OnDestroy {

  breadcrumbItems: BreadcrumbItem[] = [];
  private destroy$ = new Subject<void>();

  // ───────────────────────────────────────────────────────────────
  // Tab management
  // ───────────────────────────────────────────────────────────────
  activeTab: 'approved' | 'pending' | 'rejected' = 'approved';

  // ───────────────────────────────────────────────────────────────
  // Approved Payments Tab - Companies
  // ───────────────────────────────────────────────────────────────
  approvedCompanies: CompanyWithPayslips[] = [];
  filteredApprovedCompanies: CompanyWithPayslips[] = [];
  pagedApprovedCompanies: CompanyWithPayslips[] = [];
  approvedCompanySearchText = '';
  approvedCompaniesPage = 1;
  approvedCompaniesPageSize = 10;
  isApprovedCompaniesLoading = false;
  approvedCompaniesError = '';
  expandedApprovedCompanyIds = new Set<number>();

  // ───────────────────────────────────────────────────────────────
  // Approved Payments Tab - Payments per company (lazy, cached)
  // ───────────────────────────────────────────────────────────────
  approvedPaymentsByCompany = new Map<number, CixorPaymentPayslipSummary[]>();
  isApprovedPaymentsLoadingMap: Record<number, boolean> = {};
  approvedPaymentsErrorMap: Record<number, string> = {};

  // ───────────────────────────────────────────────────────────────
  // Pending Approval Tab - Companies
  // ───────────────────────────────────────────────────────────────
  pendingCompanies: CompanyWithPayslips[] = [];
  filteredPendingCompanies: CompanyWithPayslips[] = [];
  pagedPendingCompanies: CompanyWithPayslips[] = [];
  pendingCompanySearchText = '';
  pendingCompaniesPage = 1;
  pendingCompaniesPageSize = 10;
  isPendingCompaniesLoading = false;
  pendingCompaniesError = '';
  expandedPendingCompanyIds = new Set<number>();

  // ───────────────────────────────────────────────────────────────
  // Pending Approval Tab - Payments per company (lazy, cached)
  // ───────────────────────────────────────────────────────────────
  paymentsByCompany = new Map<number, CixorPaymentPayslipSummary[]>();
  isPaymentsLoadingMap: Record<number, boolean> = {};
  paymentsErrorMap: Record<number, string> = {};

  // ───────────────────────────────────────────────────────────────
  // Payment Details Offcanvas
  // ───────────────────────────────────────────────────────────────
  selectedCompany: CompanyWithPayslips | null = null;
  selectedPayment: CixorPaymentPayslipSummary | null = null;
  isPaymentDetailsLoading = false;
  paymentDetailsError = '';
  paymentDetails: CixorPaymentPayslipDetail | null = null;
  isApproving = false;
  isRejecting = false;

  // Actual Payment Date for approval
  actualPaymentDate = '';

  // ───────────────────────────────────────────────────────────────
  // Invoice Payment Ledger — for payment selection on approval
  // ───────────────────────────────────────────────────────────────
  invoicePayments: InvoicePaymentLedgerItem[] = [];
  selectedPaymentIds = new Set<number>();
  isPaymentsLoading = false;
  paymentsLoadError = '';

  // Rejection reason modal
  rejectionReason = '';
  rejectionReasonError = '';
  rejectionModalInstance: any = null;

  // ───────────────────────────────────────────────────────────────
  // Rejected Payments Tab - Companies
  // ───────────────────────────────────────────────────────────────
  rejectedCompanies: CompanyWithPayslips[] = [];
  filteredRejectedCompanies: CompanyWithPayslips[] = [];
  pagedRejectedCompanies: CompanyWithPayslips[] = [];
  rejectedCompanySearchText = '';
  rejectedCompaniesPage = 1;
  rejectedCompaniesPageSize = 10;
  isRejectedCompaniesLoading = false;
  rejectedCompaniesError = '';
  expandedRejectedCompanyIds = new Set<number>();

  // ───────────────────────────────────────────────────────────────
  // Rejected Payments Tab - Payments per company (lazy, cached)
  // ───────────────────────────────────────────────────────────────
  rejectedPaymentsByCompany = new Map<number, CixorPaymentPayslipSummary[]>();
  isRejectedPaymentsLoadingMap: Record<number, boolean> = {};
  rejectedPaymentsErrorMap: Record<number, string> = {};

  dataSubscriber$!: Subscription;
  constructor(
    private transactionsService: TransactionsService,
    protected authService: AuthService,
    private breadcrumbService: BreadcrumbService,
    private toastrService: ToastrService,
    private datePipe: DatePipe,
    private route: ActivatedRoute
  ) {

    this.dataSubscriber$ = new Subscription();
  }

  ngOnInit(): void {

    // Subscribe to breadcrumb changes
    this.breadcrumbService.breadcrumbs$
      .pipe(takeUntil(this.destroy$))
      .subscribe(items => {
        this.breadcrumbItems = items;
      });

    // Set breadcrumbs for this page
    this.breadcrumbService.setBreadcrumbs([
      { label: 'Dashboard', url: '/dashboard', icon: 'bi bi-house-door' },
      { label: 'Manage Payments', icon: 'bi bi-credit-card' },
      { label: 'CIXOR Payments', icon: 'bi bi-currency-exchange' }
    ]);

    const tabParam = this.route.snapshot.queryParamMap.get('tab');
    if (tabParam === 'pending' || tabParam === 'approved' || tabParam === 'rejected') {
      this.activeTab = tabParam;
    }

    this.refreshTab(this.activeTab);
    this.refreshTab('pending');
    this.refreshTab('rejected');
  }

  // ───────────────────────────────────────────────────────────────
  // Tab management
  // ───────────────────────────────────────────────────────────────
  setActiveTab(tab: 'approved' | 'pending' | 'rejected'): void {
    if (this.activeTab !== tab) {
      this.activeTab = tab;
    }
    this.refreshTab(tab);
  }

  private refreshTab(tab: 'approved' | 'pending' | 'rejected'): void {
    switch (tab) {
      case 'approved':
        this.fetchApprovedPayments();
        break;
      case 'pending':
        this.fetchPendingPayments();
        break;
      case 'rejected':
        this.fetchRejectedPayments();
        break;
    }
  }

  // ───────────────────────────────────────────────────────────────
  // Approved Payments - Companies
  // ───────────────────────────────────────────────────────────────
  fetchApprovedPayments(): void {
    this.isApprovedCompaniesLoading = true;
    this.approvedCompaniesError = '';

    this.dataSubscriber$.add(
      this.transactionsService.getCompaniesWithPayslips('Approved')
        .pipe(finalize(() => {
          this.isApprovedCompaniesLoading = false;
        }))
        .subscribe({
          next: response => {
            if (!response?.success) {
              this.handleApprovedCompaniesFailure(response?.message || 'Unable to load approved companies.');
              return;
            }

            this.approvedCompanies = response.data ?? [];
            this.expandedApprovedCompanyIds.clear();
            this.approvedPaymentsByCompany.clear();
            this.performApprovedCompanySearch();
          },
          error: error => {
            this.handleApprovedCompaniesFailure(this.getErrorMessage(error, 'Unable to load approved companies.'));
          }
        })
    );
  }

  performApprovedCompanySearch(): void {
    const q = (this.approvedCompanySearchText || '').trim().toLowerCase();
    if (!q) {
      this.filteredApprovedCompanies = [...this.approvedCompanies];
      this.resetApprovedCompaniesPagination();
      return;
    }
    this.filteredApprovedCompanies = this.approvedCompanies.filter(c =>
      this.toStr(c.companyName).toLowerCase().includes(q) ||
      this.toStr(c.companyId).toLowerCase().includes(q)
    );
    this.resetApprovedCompaniesPagination();
  }

  isApprovedCompanyExpanded(companyId: number): boolean {
    return this.expandedApprovedCompanyIds.has(companyId);
  }

  toggleApprovedCompanyExpand(company: CompanyWithPayslips): void {
    const id = Number(company.companyId);
    if (this.isApprovedCompanyExpanded(id)) {
      this.expandedApprovedCompanyIds.delete(id);
      return;
    }
    this.expandedApprovedCompanyIds.add(id);
    if (!this.approvedPaymentsByCompany.has(id)) {
      this.loadApprovedPaymentsForCompany(id);
    }
  }

  private loadApprovedPaymentsForCompany(companyId: number): void {
    this.loadCompanyPayslipSummary({
      companyId,
      status: 'Approved',
      targetMap: this.approvedPaymentsByCompany,
      loadingMap: this.isApprovedPaymentsLoadingMap,
      errorMap: this.approvedPaymentsErrorMap,
      fallbackMessage: 'Unable to load approved payments for this company.'
    });
  }

  private updateApprovedCompaniesPage(): void {
    if (this.approvedCompaniesPage < 1) this.approvedCompaniesPage = 1;
    const start = (this.approvedCompaniesPage - 1) * this.approvedCompaniesPageSize;
    this.pagedApprovedCompanies = this.filteredApprovedCompanies.slice(start, start + this.approvedCompaniesPageSize);
  }

  private resetApprovedCompaniesPagination(): void {
    this.approvedCompaniesPage = 1;
    this.updateApprovedCompaniesPage();
  }

  onApprovedCompaniesPageChange(paginatedData: any[]): void {
    this.pagedApprovedCompanies = paginatedData;
  }

  // ───────────────────────────────────────────────────────────────
  // Pending Approval - Companies
  // ───────────────────────────────────────────────────────────────
  fetchPendingPayments(): void {
    this.isPendingCompaniesLoading = true;
    this.pendingCompaniesError = '';

    this.dataSubscriber$.add(
      this.transactionsService.getCompaniesWithPayslips('Pending')
        .pipe(finalize(() => {
          this.isPendingCompaniesLoading = false;
        }))
        .subscribe({
          next: response => {
            if (!response?.success) {
              this.handlePendingCompaniesFailure(response?.message || 'Unable to load pending companies.');
              return;
            }

            this.pendingCompanies = response.data ?? [];
            this.expandedPendingCompanyIds.clear();
            this.paymentsByCompany.clear();
            this.performPendingCompanySearch();
            // Pre-fetch payslips for all companies so the tab badge shows total record count
            this.pendingCompanies.forEach(c => this.loadPaymentsForCompany(Number(c.companyId)));
          },
          error: error => {
            this.handlePendingCompaniesFailure(this.getErrorMessage(error, 'Unable to load pending companies.'));
          }
        })
    );
  }

  performPendingCompanySearch(): void {
    const q = (this.pendingCompanySearchText || '').trim().toLowerCase();
    if (!q) {
      this.filteredPendingCompanies = [...this.pendingCompanies];
      this.resetPendingCompaniesPagination();
      return;
    }
    this.filteredPendingCompanies = this.pendingCompanies.filter(c =>
      this.toStr(c.companyName).toLowerCase().includes(q) ||
      this.toStr(c.companyId).toLowerCase().includes(q)
    );
    this.resetPendingCompaniesPagination();
  }

  isPendingCompanyExpanded(companyId: number): boolean {
    return this.expandedPendingCompanyIds.has(companyId);
  }

  togglePendingCompanyExpand(company: CompanyWithPayslips): void {
    const id = Number(company.companyId);
    if (this.isPendingCompanyExpanded(id)) {
      this.expandedPendingCompanyIds.delete(id);
      return;
    }
    this.expandedPendingCompanyIds.add(id);
    if (!this.paymentsByCompany.has(id)) {
      this.loadPaymentsForCompany(id);
    }
  }

  private loadPaymentsForCompany(companyId: number): void {
    this.loadCompanyPayslipSummary({
      companyId,
      status: 'Pending',
      targetMap: this.paymentsByCompany,
      loadingMap: this.isPaymentsLoadingMap,
      errorMap: this.paymentsErrorMap,
      fallbackMessage: 'Unable to load pending payments for this company.'
    });
  }

  private updatePendingCompaniesPage(): void {
    if (this.pendingCompaniesPage < 1) this.pendingCompaniesPage = 1;
    const start = (this.pendingCompaniesPage - 1) * this.pendingCompaniesPageSize;
    this.pagedPendingCompanies = this.filteredPendingCompanies.slice(start, start + this.pendingCompaniesPageSize);
  }

  private resetPendingCompaniesPagination(): void {
    this.pendingCompaniesPage = 1;
    this.updatePendingCompaniesPage();
  }

  onPendingCompaniesPageChange(paginatedData: any[]): void {
    this.pagedPendingCompanies = paginatedData;
  }

  // ───────────────────────────────────────────────────────────────
  // Payment Details Offcanvas
  // ───────────────────────────────────────────────────────────────
  openPaymentDetails(company: CompanyWithPayslips, payment: CixorPaymentPayslipSummary): void {
    this.selectedCompany = company;
    this.selectedPayment = this.normalizeSummaryPayment(payment);
    this.paymentDetailsError = '';
    this.paymentDetails = null;
    this.actualPaymentDate = '';
    this.invoicePayments = [];
    this.selectedPaymentIds.clear();
    this.isPaymentsLoading = false;
    this.paymentsLoadError = '';
    this.fetchPaymentDetails();
  }

  fetchPaymentDetails(): void {
    if (!this.selectedCompany || !this.selectedPayment) {
      return;
    }

    const companyId = Number(this.selectedCompany.companyId);
    const paySlipId = Number(this.selectedPayment.paySlipId);

    if (!companyId || !paySlipId) {
      this.paymentDetailsError = 'Unable to identify the selected payment.';
      return;
    }

    const activeTabStatus = this.activeTab === 'approved' ? 'Approved' : this.activeTab === 'pending' ? 'Pending' : 'Rejected';

    if (this.activeTab === 'rejected') {
      this.transactionsService.getCompanyPayslipDetails(companyId, paySlipId, 'Rejected')
        .pipe(finalize(() => {
          this.isPaymentDetailsLoading = false;
        }))
        .subscribe({
          next: response => {
            if (!response?.success) {
              this.paymentDetails = null;
              this.paymentDetailsError = response?.message || 'Unable to load payment details.';
              return;
            }

            const detail = response.data?.[0];
            if (!detail) {
              this.paymentDetails = null;
              this.paymentDetailsError = 'Payment details not found.';
              return;
            }

            this.paymentDetails = this.mapPayslipDetail(detail);
            if (detail.actualPaymentDate) {
              this.actualPaymentDate = detail.actualPaymentDate.slice(0, 10);
            }
          },
          error: error => {
            this.paymentDetails = null;
            this.paymentDetailsError = this.getErrorMessage(error, 'Unable to load payment details.');
          }
        });
      return;
    }

    this.isPaymentDetailsLoading = true;
    this.paymentDetailsError = '';

    this.dataSubscriber$.add(
      this.transactionsService.getCompanyPayslipDetails(companyId, paySlipId, activeTabStatus)
        .pipe(finalize(() => {
          this.isPaymentDetailsLoading = false;
        }))
        .subscribe({
          next: response => {
            if (!response?.success) {
              this.paymentDetails = null;
              this.paymentDetailsError = response?.message || 'Unable to load payment details.';
              return;
            }

            const detail = response.data?.[0];
            if (!detail) {
              this.paymentDetails = null;
              this.paymentDetailsError = 'Payment details not found.';
              return;
            }

            this.paymentDetails = this.mapPayslipDetail(detail);
            if (detail.actualPaymentDate) {
              this.actualPaymentDate = detail.actualPaymentDate.slice(0, 10);
            }
            // Load invoice payment ledger for non-rollover invoices (pending tab only)
            if (this.activeTab === 'pending') {
              const itype = (detail.invoiceType ?? '').toUpperCase();
              if (itype !== 'ROLLOVER-IMMEDIATE-PAYMENT' && itype !== 'ROLLOVER-REPAYMENT') {
                this.loadPendingPayments();
              }
            }
          },
          error: error => {
            this.paymentDetails = null;
            this.paymentDetailsError = this.getErrorMessage(error, 'Unable to load payment details.');
          }
        })
    );
  }

  approvePayment(): void {
    if (!this.selectedCompany || !this.selectedPayment) return;

    const companyId = Number(this.selectedCompany.companyId);
    const payslipId = Number(this.selectedPayment.paySlipId);
    const approvedBy = this.getCurrentUserId();

    if (!companyId || !payslipId) {
      this.paymentDetailsError = 'Unable to identify the selected payment.';
      return;
    }

    if (!this.actualPaymentDate) {
      this.paymentDetailsError = 'Actual Payment Date is required.';
      return;
    }

    // Require at least one payment selected when the ledger table is populated
    if (!this.isRolloverInvoice && this.invoicePayments.length > 0 && this.selectedPaymentIds.size === 0) {
      this.paymentDetailsError = 'Please select at least one payment to approve.';
      return;
    }

    const paymentIds = !this.isRolloverInvoice && this.selectedPaymentIds.size > 0
      ? Array.from(this.selectedPaymentIds)
      : undefined;

    this.isApproving = true;
    this.paymentDetailsError = '';

    this.dataSubscriber$.add(
      this.transactionsService.approveCixorPaymentPayslip(companyId, payslipId, approvedBy, this.actualPaymentDate, paymentIds)
        .pipe(finalize(() => {
          this.isApproving = false;
        }))
        .subscribe({
          next: response => {
            if (!response?.success) {
              this.paymentDetailsError = response?.data?.message || 'Failed to approve payment.';
              this.toastrService.error(this.paymentDetailsError, 'Error');
              return;
            }

            this.toastrService.success(response?.data?.message || 'Payment approved successfully.', 'Success');
            // Close offcanvas
            this.closePaymentDetailsOffcanvas();

            // Refresh pending payments
            this.refreshTab(this.activeTab);
            this.refreshTab('pending');
          },
          error: error => {
            this.paymentDetailsError = this.getErrorMessage(error, 'Failed to approve payment.');
            this.toastrService.error(this.paymentDetailsError, 'Error');
          }
        })
    );
  }

  openRejectionModal(): void {
    if (!this.selectedCompany || !this.selectedPayment) return;
    this.rejectionReason = '';
    this.rejectionReasonError = '';

    try {
      const modalElement = document.getElementById('rejectionModal');
      if (modalElement) {
        if (!this.rejectionModalInstance) {
          this.rejectionModalInstance = new bootstrap.Modal(modalElement, {
            backdrop: 'static',
            keyboard: false
          });
        }
        this.rejectionModalInstance.show();
      }
    } catch (error) {
      console.error('Error opening rejection modal:', error);
    }
  }

  closeRejectionModal(): void {
    try {
      if (this.rejectionModalInstance) {
        this.rejectionModalInstance.hide();
      }
    } catch (error) {
      console.error('Error closing rejection modal:', error);
    }
    this.rejectionReason = '';
    this.rejectionReasonError = '';
  }

  rejectPayment(): void {
    if (!this.selectedCompany || !this.selectedPayment) return;

    // Validate rejection reason
    const trimmedReason = (this.rejectionReason || '').trim();
    if (!trimmedReason) {
      this.rejectionReasonError = 'Rejection reason is required.';
      return;
    }

    const companyId = Number(this.selectedCompany.companyId);
    const payslipId = Number(this.selectedPayment.paySlipId);
    const rejectedBy = this.getCurrentUserId();

    if (!companyId || !payslipId) {
      this.rejectionReasonError = 'Unable to identify the selected payment.';
      this.toastrService.error(this.rejectionReasonError, 'Error');
      return;
    }

    this.isRejecting = true;
    this.rejectionReasonError = '';

    this.dataSubscriber$.add(
      this.transactionsService.rejectCixorPaymentPayslip(companyId, payslipId, trimmedReason, rejectedBy)
        .pipe(finalize(() => {
          this.isRejecting = false;
        }))
        .subscribe({
          next: response => {
            if (!response?.success) {
              this.rejectionReasonError = response?.data?.message || 'Failed to reject payment.';
              this.toastrService.error(this.rejectionReasonError, 'Error');
              return;
            }

            this.toastrService.success(response?.data?.message || 'Payment rejected successfully.', 'Success');
            // Close rejection modal first
            this.closeRejectionModal();

            // Close offcanvas
            this.closePaymentDetailsOffcanvas();

            // Refresh pending payments
            this.refreshTab(this.activeTab);
          },
          error: error => {
            this.rejectionReasonError = this.getErrorMessage(error, 'Failed to reject payment.');
            this.toastrService.error(this.rejectionReasonError, 'Error');
          }
        })
    );
  }

  private getCurrentUserId(): number {
    const user = this.authService.getUser();
    const userId = user?.portalAdminId;
    return (typeof userId === 'number' ? userId : Number(userId)) || 0;
  }

  private closePaymentDetailsOffcanvas(): void {
    const offcanvasElement = document.getElementById('paymentDetailsOffcanvas');
    if (offcanvasElement) {
      const bsOffcanvas = (window as any).bootstrap?.Offcanvas?.getInstance(offcanvasElement);
      if (bsOffcanvas) {
        bsOffcanvas.hide();
      }
    }
  }

  // ───────────────────────────────────────────────────────────────
  // Rejected Payments - Companies
  // ───────────────────────────────────────────────────────────────
  fetchRejectedPayments(): void {
    this.isRejectedCompaniesLoading = true;
    this.rejectedCompaniesError = '';

    this.dataSubscriber$.add(
      this.transactionsService.getCompaniesWithPayslips('Rejected')
        .pipe(finalize(() => {
          this.isRejectedCompaniesLoading = false;
        }))
        .subscribe({
          next: response => {
            if (!response?.success) {
              this.handleRejectedCompaniesFailure(response?.message || 'Unable to load rejected companies.');
              return;
            }

            this.rejectedCompanies = response.data ?? [];
            this.expandedRejectedCompanyIds.clear();
            this.rejectedPaymentsByCompany.clear();
            this.performRejectedCompanySearch();
          },
          error: error => {
            this.handleRejectedCompaniesFailure(this.getErrorMessage(error, 'Unable to load rejected companies.'));
          }
        })
    );
  }

  performRejectedCompanySearch(): void {
    const q = (this.rejectedCompanySearchText || '').trim().toLowerCase();
    if (!q) {
      this.filteredRejectedCompanies = [...this.rejectedCompanies];
      this.resetRejectedCompaniesPagination();
      return;
    }
    this.filteredRejectedCompanies = this.rejectedCompanies.filter(c =>
      this.toStr(c.companyName).toLowerCase().includes(q) ||
      this.toStr(c.companyId).toLowerCase().includes(q)
    );
    this.resetRejectedCompaniesPagination();
  }

  isRejectedCompanyExpanded(companyId: number): boolean {
    return this.expandedRejectedCompanyIds.has(companyId);
  }

  toggleRejectedCompanyExpand(company: any): void {
    const id = Number(company.companyId);
    if (this.isRejectedCompanyExpanded(id)) {
      this.expandedRejectedCompanyIds.delete(id);
      return;
    }
    this.expandedRejectedCompanyIds.add(id);
    if (!this.rejectedPaymentsByCompany.has(id)) {
      this.loadRejectedPaymentsForCompany(id);
    }
  }

  private loadRejectedPaymentsForCompany(companyId: number): void {
    this.loadCompanyPayslipSummary({
      companyId,
      status: 'Rejected',
      targetMap: this.rejectedPaymentsByCompany,
      loadingMap: this.isRejectedPaymentsLoadingMap,
      errorMap: this.rejectedPaymentsErrorMap,
      fallbackMessage: 'Unable to load rejected payments for this company.'
    });
  }

  private updateRejectedCompaniesPage(): void {
    if (this.rejectedCompaniesPage < 1) this.rejectedCompaniesPage = 1;
    const start = (this.rejectedCompaniesPage - 1) * this.rejectedCompaniesPageSize;
    this.pagedRejectedCompanies = this.filteredRejectedCompanies.slice(start, start + this.rejectedCompaniesPageSize);
  }

  private resetRejectedCompaniesPagination(): void {
    this.rejectedCompaniesPage = 1;
    this.updateRejectedCompaniesPage();
  }

  onRejectedCompaniesPageChange(paginatedData: any[]): void {
    this.pagedRejectedCompanies = paginatedData;
  }

  // ───────────────────────────────────────────────────────────────
  // Helpers
  // ───────────────────────────────────────────────────────────────
  get todayDateString(): string {
    const today = new Date();
    const yyyy = today.getFullYear();
    const MM = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return `${yyyy}-${MM}-${dd}`;
  }

  private toStr(v: any): string {
    return v === null || v === undefined ? '' : String(v);
  }

  formatDateTime(date?: string | Date): string {
    if (!date) return '';

    const d = new Date(date);

    const yyyy = d.getFullYear();
    const MM = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');

    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');

    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12; // convert 0 → 12

    return `${yyyy}-${MM}-${dd}, ${hours}:${minutes}:${seconds} ${ampm}`;
  }

  openImageInNewTab(url: string, type?: string, salaryCycle?: string | number, issueDate?: string): void {
    if (!url) {
      console.error('No image URL provided');
      return;
    }

    const newTab = window.open('', '_blank');

    if (newTab) {
      newTab.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Payment Slip</title>
          <style>
            body { 
              margin: 0; 
              padding: 0;
              display: flex; 
              justify-content: center; 
              align-items: center; 
              min-height: 100vh; 
              background: #1a1a1a; 
            }
            img { 
              max-width: 95%; 
              max-height: 95vh; 
              object-fit: contain;
              box-shadow: 0 4px 20px rgba(0,0,0,0.5);
            }
          </style>
        </head>
        <body>
          <img src="${url}" alt="Payment Slip" onload="document.title='Payment Slip - Loaded'" onerror="document.body.innerHTML='<p style=color:white;>Failed to load image</p>'" />
        </body>
      </html>
    `);
      newTab.document.close();
      const issueDateConverted = this.datePipe.transform(issueDate, 'yyyy-MM-dd')?.toString();
      this.downloadImage(url, type, salaryCycle, issueDateConverted);
    } else {
      this.toastrService.error('Unable to open image. Please allow popups for this site.', 'error');
    }
  }

  downloadImage(url: string, type?: string, salaryCycle?: string | number, issueDate?: string): void {
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.download = `payment-slip-${type}-${salaryCycle}-issue-date-${issueDate}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  private handleApprovedCompaniesFailure(message: string): void {
    this.approvedCompanies = [];
    this.filteredApprovedCompanies = [];
    this.resetApprovedCompaniesPagination();
    this.expandedApprovedCompanyIds.clear();
    this.approvedPaymentsByCompany.clear();
    this.approvedCompaniesError = message;
  }

  private handlePendingCompaniesFailure(message: string): void {
    this.pendingCompanies = [];
    this.filteredPendingCompanies = [];
    this.resetPendingCompaniesPagination();
    this.expandedPendingCompanyIds.clear();
    this.paymentsByCompany.clear();
    this.pendingCompaniesError = message;
  }

  private handleRejectedCompaniesFailure(message: string): void {
    this.rejectedCompanies = [];
    this.filteredRejectedCompanies = [];
    this.resetRejectedCompaniesPagination();
    this.expandedRejectedCompanyIds.clear();
    this.rejectedPaymentsByCompany.clear();
    this.rejectedCompaniesError = message;
  }

  private getErrorMessage(error: any, fallback: string): string {
    const apiMessage = error?.error?.message || error?.error?.Message;
    if (apiMessage) {
      return apiMessage;
    }
    if (typeof error?.message === 'string' && error.message.trim()) {
      return error.message;
    }
    return fallback;
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Invoice Payment Ledger helpers
  // ───────────────────────────────────────────────────────────────────────────

  get isRolloverInvoice(): boolean {
    const type = (this.paymentDetails?.invoiceType ?? this.selectedPayment?.invoiceType ?? '').toUpperCase();
    return type === 'ROLLOVER-IMMEDIATE-PAYMENT' || type === 'ROLLOVER-REPAYMENT';
  }

  /** Returns the "Amount Settled" value for a summary row.
   *  Rollover payments have no ledger entries so newlyPaid=0; use paidAmount instead. */
  getAmountSettled(payment: CixorPaymentPayslipSummary): number {
    const type = (payment.invoiceType ?? '').toUpperCase();
    const isRollover = type === 'ROLLOVER-IMMEDIATE-PAYMENT' || type === 'ROLLOVER-REPAYMENT';
    return isRollover ? (payment.paidAmount ?? 0) : (payment.newlyPaid ?? 0);
  }

  get allPaymentsSelected(): boolean {
    return this.invoicePayments.length > 0 &&
      this.invoicePayments.every(p => this.selectedPaymentIds.has(p.paymentId));
  }

  get somePaymentsSelected(): boolean {
    return this.selectedPaymentIds.size > 0 && !this.allPaymentsSelected;
  }

  togglePaymentSelection(paymentId: number): void {
    if (this.selectedPaymentIds.has(paymentId)) {
      this.selectedPaymentIds.delete(paymentId);
    } else {
      this.selectedPaymentIds.add(paymentId);
    }
  }

  toggleAllPayments(): void {
    if (this.allPaymentsSelected) {
      this.selectedPaymentIds.clear();
    } else {
      this.invoicePayments.forEach(p => this.selectedPaymentIds.add(p.paymentId));
    }
  }

  private loadPendingPayments(): void {
    if (!this.selectedCompany || !this.selectedPayment) return;
    const companyId = Number(this.selectedCompany.companyId);
    const payslipId = Number(this.selectedPayment.paySlipId);
    if (!companyId || !payslipId) return;

    this.invoicePayments = [];
    this.selectedPaymentIds.clear();
    this.isPaymentsLoading = true;
    this.paymentsLoadError = '';

    this.dataSubscriber$.add(
      this.transactionsService.getPendingPaymentsForPayslip(companyId, payslipId)
        .pipe(finalize(() => { this.isPaymentsLoading = false; }))
        .subscribe({
          next: response => {
            if (!response?.success) {
              this.paymentsLoadError = response?.message || 'Unable to load payments.';
              return;
            }
            this.invoicePayments = response.data ?? [];
            // Pre-select all by default
            this.invoicePayments.forEach(p => this.selectedPaymentIds.add(p.paymentId));
          },
          error: error => {
            this.paymentsLoadError = this.getErrorMessage(error, 'Unable to load payments.');
          }
        })
    );
  }

  get pendingPayslipTotalCount(): number {
    let total = 0;
    for (const payslips of this.paymentsByCompany.values()) {
      total += payslips.length;
    }
    return total;
  }

  private loadCompanyPayslipSummary(params: {
    companyId: number;
    status: PayslipCompanyStatus;
    targetMap: Map<number, CixorPaymentPayslipSummary[]>;
    loadingMap: Record<number, boolean>;
    errorMap: Record<number, string>;
    fallbackMessage: string;
  }): void {
    const { companyId, status, targetMap, loadingMap, errorMap, fallbackMessage } = params;

    loadingMap[companyId] = true;
    errorMap[companyId] = '';

    this.dataSubscriber$.add(

      this.transactionsService.getCompanyPayslipSummary(companyId, status)
        .pipe(finalize(() => {
          loadingMap[companyId] = false;
        }))
        .subscribe({
          next: response => {
            if (!response?.success) {
              errorMap[companyId] = response?.message || fallbackMessage;
              targetMap.set(companyId, []);
              return;
            }

            targetMap.set(companyId, this.normalizeSummaryPayments(response.data));
          },
          error: error => {
            errorMap[companyId] = this.getErrorMessage(error, fallbackMessage);
            targetMap.set(companyId, []);
          }
        })
    );
  }

  private mapPayslipDetail(detail: CixorPaymentPayslipDetail): CixorPaymentPayslipDetail {
    const normalizedSlip = this.normalizeSlipImage(detail.paymentSlip ?? detail.payslipUrl ?? this.selectedPayment?.paymentSlip ?? this.selectedPayment?.paymentSlipUrl);
    return {
      paySlipId: detail.paySlipId ?? detail.cixorPaymentPayslipId ?? this.selectedPayment?.paySlipId ?? 0,
      companyId: detail.companyId ?? Number(this.selectedCompany?.companyId ?? 0),
      companyName: detail.companyName ?? this.selectedCompany?.companyName ?? '',
      invoiceId: detail.invoiceId ?? this.selectedPayment?.invoiceId,
      invoiceType: detail.invoiceType ?? this.selectedPayment?.invoiceType,
      salaryCycle: detail.salaryCycle ?? detail.salaryCycleName ?? this.selectedPayment?.salaryCycle,
      issueDate: detail.issueDate ?? this.selectedPayment?.issueDate,
      dueDate: detail.dueDate ?? this.selectedPayment?.dueDate,
      totalAmount: detail.totalAmount ?? this.selectedPayment?.totalAmount,
      paidAmount: detail.paidAmount ?? this.selectedPayment?.paidAmount,
      balance: detail.balance ?? this.selectedPayment?.balance,
      referenceNumber: detail.referenceNumber ?? detail.referenceCompact ?? this.selectedPayment?.referenceNumber ?? detail.invoiceNumber,
      invoiceNumber: detail.invoiceNumber,
      invoiceStatus: detail.invoiceStatus,
      payslipStatus: detail.payslipStatus,
      isApproved: detail.isApproved,
      isRejected: detail.isRejected,
      rejectionReason: detail.rejectionReason ?? '',
      paymentSlip: normalizedSlip,
      paymentSlipUrl: normalizedSlip,
      createdDateUtc: detail.createdDateUtc,
      updatedDateUtc: detail.updatedDateUtc,
      actualPaymentDate: detail.actualPaymentDate,
      lateFee: detail.lateFee,
      lateFeeAddInvoiceNumber: detail.lateFeeAddInvoiceNumber,
      newlyPaid: detail.newlyPaid ?? this.selectedPayment?.newlyPaid,
      previouslyPaid: detail.previouslyPaid ?? this.selectedPayment?.previouslyPaid
    };
  }

  private normalizeSummaryPayments(payments: CixorPaymentPayslipSummary[] | undefined): CixorPaymentPayslipSummary[] {
    return (payments ?? []).map(payment => this.normalizeSummaryPayment(payment));
  }

  private normalizeSummaryPayment(payment: CixorPaymentPayslipSummary): CixorPaymentPayslipSummary {
    const normalizedSlip = this.normalizeSlipImage(payment.paymentSlip ?? payment.paymentSlipUrl);
    return {
      ...payment,
      paymentSlip: normalizedSlip,
      paymentSlipUrl: normalizedSlip ?? payment.paymentSlipUrl ?? payment.paymentSlip
    };
  }

  private normalizeSlipImage(imageValue?: string | null): string | undefined {
    if (!imageValue) {
      return undefined;
    }
    const trimmed = imageValue.trim();
    if (!trimmed) {
      return undefined;
    }
    if (/^(data:|https?:\/\/)/i.test(trimmed)) {
      return trimmed;
    }
    return `data:image/png;base64,${trimmed}`;
  }

  ngOnDestroy(): void {
    this.dataSubscriber$.unsubscribe();
  }
}
